import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const AddPaintSheet = ({ open, onOpenChange, onSave }) => {
    const [room, setRoom] = useState("");
    const [brand, setBrand] = useState("");
    const [code, setCode] = useState("");
    const [finish, setFinish] = useState("");
    const [lastPainted, setLastPainted] = useState("");
    const [loading, setLoading] = useState(false);

    const finishes = ["Matte", "Semi-Gloss", "Gloss", "Satin", "Eggshell", "Suede"];

    const resetForm = () => {
        setRoom("");
        setBrand("");
        setCode("");
        setFinish("");
        setLastPainted("");
    };

    const handleSave = async () => {
        if (!room || !brand || !code || !finish) return;

        setLoading(true);
        const success = await onSave({
            room,
            brand,
            code,
            finish,
            lastPainted: lastPainted || undefined,
        });

        if (success) {
            resetForm();
            onOpenChange(false);
        }
        setLoading(false);
    };

    const handleClose = (open) => {
        if (!open) resetForm();
        onOpenChange(open);
    };

    return (
        <Sheet open={open} onOpenChange={handleClose}>
            <SheetContent
                side="bottom"
                onOpenAutoFocus={(e) => e.preventDefault()}
                className="max-h-[85vh] rounded-t-3xl flex flex-col p-0"
                style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}
            >
                <SheetHeader className="pb-4 flex-shrink-0 px-6 pt-6">
                    <SheetTitle className="text-xl font-bold text-foreground">
                        Add Wall Paint Code
                    </SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 space-y-4 pb-4 px-6">
                    <div className="space-y-2">
                        <Label htmlFor="paint-room">Room *</Label>
                        <Input
                            id="paint-room"
                            value={room}
                            onChange={(e) => setRoom(e.target.value)}
                            placeholder="e.g., Living Room, Master Bedroom"
                            className="rounded-xl w-full"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="paint-brand">Brand *</Label>
                        <Input
                            id="paint-brand"
                            value={brand}
                            onChange={(e) => setBrand(e.target.value)}
                            placeholder="e.g., Plascon, Dulux"
                            className="rounded-xl w-full"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="paint-code">Paint Code / Color Name *</Label>
                        <Input
                            id="paint-code"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            placeholder="e.g., Evening Mist, SW 7015"
                            className="rounded-xl w-full"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label>Finish *</Label>
                        <Select value={finish} onValueChange={setFinish}>
                            <SelectTrigger className="rounded-xl w-full">
                                <SelectValue placeholder="Select finish" />
                            </SelectTrigger>
                            <SelectContent>
                                {finishes.map((f) => (
                                    <SelectItem key={f} value={f}>
                                        {f}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="paint-date">Last Painted (Optional)</Label>
                        <Input
                            id="paint-date"
                            type="date"
                            value={lastPainted}
                            onChange={(e) => setLastPainted(e.target.value)}
                            className="rounded-xl w-full"
                        />
                    </div>
                </div>

                <div className="flex-shrink-0 pt-2 px-6">
                    <Button
                        onClick={handleSave}
                        className="w-full rounded-xl py-5"
                        disabled={!room || !brand || !code || !finish || loading}
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Paint Code"}
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default AddPaintSheet;
